/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  LinearProgress,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Lottie from 'react-lottie';
import pearlLoginLogo from 'assets/images/pearl-loginpage.png';
import successAnimation from 'assets/animations/success-animation.json';
import { useAuth } from 'auth/Auth';
import colors from 'styles/tokens/colors';
import { buildAppLoginUrl } from 'utils/tenantUrl';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '11px',
    bgcolor: colors.loginBrand.surface,
    '& fieldset': { borderColor: colors.loginBrand.line },
    '&:hover fieldset': { borderColor: colors.loginBrand.accent },
    '&.Mui-focused fieldset': {
      borderColor: colors.loginBrand.accent,
      boxShadow: `0 0 0 3px ${colors.loginBrand.accentSoft}`,
    },
  },
};

const ValidationRow = ({ valid, label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.35 }}>
    {valid ? (
      <CheckIcon sx={{ fontSize: 16, color: colors.success.main }} />
    ) : (
      <CancelOutlinedIcon sx={{ fontSize: 16, color: colors.loginBrand.muted }} />
    )}
    <Typography sx={{ fontSize: 13, color: valid ? colors.success.main : colors.loginBrand.ink2 }}>
      {label}
    </Typography>
  </Box>
);

function ResetPassword() {
  const location = useLocation();
  const { axiosInstance } = useAuth();

  const [isValidatingToken, setIsValidatingToken] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [validations, setValidations] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
    match: false,
  });
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const token = new URLSearchParams(location.search).get('token');

  const validateToken = async (rawToken) => {
    try {
      const decodedToken = decodeURIComponent(rawToken);
      const response = await axiosInstance.get(
        `/validate-reset-token/${encodeURIComponent(decodedToken)}`
      );
      return response.data.valid;
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Invalid or expired reset link',
        severity: 'error',
      });
      return false;
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        setIsValidatingToken(true);
        if (!token) {
          window.location.href = buildAppLoginUrl();
          return;
        }
        const isValid = await validateToken(token);
        setIsTokenValid(isValid);
      } catch {
        setIsTokenValid(false);
      } finally {
        setIsValidatingToken(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, token]);

  const validatePassword = (nextPassword, nextConfirmPassword) => {
    const nextValidations = {
      length: nextPassword.length >= 8,
      uppercase: /[A-Z]/.test(nextPassword),
      lowercase: /[a-z]/.test(nextPassword),
      number: /[0-9]/.test(nextPassword),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(nextPassword),
      match: nextPassword === nextConfirmPassword && nextConfirmPassword.length > 0,
    };
    setValidations(nextValidations);
    const strength = Object.values(nextValidations).filter(Boolean).length * 16.67;
    setPasswordStrength(Math.min(strength, 100));
    return Object.values(nextValidations).every(Boolean);
  };

  const goToLogin = () => {
    window.location.href = buildAppLoginUrl();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validatePassword(password, confirmPassword)) return;

    setIsSubmitting(true);
    try {
      await axiosInstance.post('/reset-password/', {
        token,
        new_password: password,
      });

      setShowSuccessAnimation(true);
      setSnackbar({
        open: true,
        message: 'Password reset successfully. Redirecting to sign in…',
        severity: 'success',
      });
      setTimeout(() => {
        goToLogin();
      }, 1800);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Failed to reset password',
        severity: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isValidatingToken) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: colors.loginBrand.cream,
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={36} sx={{ color: colors.loginBrand.accent }} />
          <Typography sx={{ mt: 2, fontSize: 15, color: colors.loginBrand.ink2 }}>
            Verifying your secure link…
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!isTokenValid) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: colors.loginBrand.cream,
          px: 3,
        }}
      >
        <Box
          sx={{
            maxWidth: 420,
            width: '100%',
            textAlign: 'center',
            bgcolor: colors.loginBrand.surface2,
            border: `1px solid ${colors.loginBrand.line}`,
            borderRadius: '16px',
            p: 4,
            boxShadow: '0 16px 48px rgba(14,35,72,0.12)',
          }}
        >
          <Box
            component="img"
            src={pearlLoginLogo}
            alt="PearlCRM"
            sx={{ height: 72, width: 'auto', maxWidth: 260, objectFit: 'contain', mb: 2 }}
          />
          <Typography
            sx={{
              fontFamily: '"Sora", sans-serif',
              fontSize: 28,
              fontWeight: 700,
              color: colors.error.text,
              mb: 1,
            }}
          >
            Link expired
          </Typography>
          <Typography sx={{ fontSize: 15, color: colors.loginBrand.ink2, mb: 3 }}>
            This password reset link is invalid or has expired. Request a new one from the sign-in
            page.
          </Typography>
          <Button
            variant="contained"
            onClick={goToLogin}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '11px',
              px: 3,
              py: 1.25,
              background: colors.loginBrand.goldGradient,
              color: colors.loginBrand.accentInk,
              boxShadow: 'none',
              '&:hover': {
                background: colors.loginBrand.goldGradient,
                boxShadow: '0 8px 24px rgba(230,138,6,0.25)',
              },
            }}
          >
            Back to sign in
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          px: { xs: 4, sm: 8, md: 12 },
          pt: { xs: 3, sm: 4 },
          pb: 4,
          bgcolor: colors.loginBrand.cream,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: { xs: 3, sm: 4 },
          }}
        >
          <Box
            component="img"
            src={pearlLoginLogo}
            alt="PearlCRM"
            sx={{ height: { xs: 64, sm: 120 }, width: 'auto', maxWidth: 320, objectFit: 'contain' }}
          />
          <Typography
            onClick={goToLogin}
            sx={{
              fontSize: 14,
              fontWeight: 500,
              color: colors.loginBrand.ink2,
              cursor: 'pointer',
              '&:hover': { color: colors.loginBrand.accent },
            }}
          >
            ← Back to sign in
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            maxWidth: 440,
            width: '100%',
            mx: 'auto',
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Sora", sans-serif',
              fontSize: { xs: 30, md: 38 },
              fontWeight: 700,
              letterSpacing: '-0.02em',
              color: colors.loginBrand.ink,
              mb: 1,
            }}
          >
            Reset your password
          </Typography>
          <Typography sx={{ fontSize: 16, color: colors.loginBrand.ink2, mb: 4 }}>
            Choose a strong new password for your PearlCRM account. You&apos;ll use it the next time
            you sign in.
          </Typography>

          <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: colors.loginBrand.ink2, mb: 0.75 }}>
            New password
          </Typography>
          <TextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              validatePassword(e.target.value, confirmPassword);
            }}
            placeholder="Enter a strong password"
            sx={{ ...fieldSx, mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((v) => !v)}
                    edge="end"
                    aria-label="toggle password visibility"
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box
            sx={{
              mb: 2.5,
              p: 2,
              borderRadius: '12px',
              bgcolor: colors.loginBrand.surface,
              border: `1px solid ${colors.loginBrand.line}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: colors.loginBrand.ink2 }}>
                Password strength
              </Typography>
              <Typography sx={{ fontSize: 12, color: colors.loginBrand.muted }}>
                {Math.round(passwordStrength)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={passwordStrength}
              sx={{
                height: 8,
                borderRadius: 999,
                bgcolor: 'rgba(14,35,72,0.08)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 999,
                  background:
                    passwordStrength < 50
                      ? 'linear-gradient(90deg, #E68A06, #FFB400)'
                      : `linear-gradient(90deg, ${colors.loginBrand.accent}, #3d93e8)`,
                },
              }}
            />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 0.5,
                mt: 1.5,
              }}
            >
              <ValidationRow valid={validations.length} label="8+ characters" />
              <ValidationRow valid={validations.uppercase} label="Uppercase letter" />
              <ValidationRow valid={validations.lowercase} label="Lowercase letter" />
              <ValidationRow valid={validations.number} label="Number" />
              <ValidationRow valid={validations.special} label="Special character" />
              <ValidationRow valid={validations.match} label="Passwords match" />
            </Box>
          </Box>

          <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: colors.loginBrand.ink2, mb: 0.75 }}>
            Confirm password
          </Typography>
          <TextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              validatePassword(password, e.target.value);
            }}
            placeholder="Re-enter your password"
            error={!!confirmPassword && !validations.match}
            helperText={confirmPassword && !validations.match ? "Passwords don't match" : ' '}
            sx={{ ...fieldSx, mb: 3 }}
          />

          <Button
            fullWidth
            type="submit"
            disabled={!Object.values(validations).every(Boolean) || isSubmitting}
            sx={{
              height: 48,
              textTransform: 'none',
              fontSize: 15,
              fontWeight: 700,
              borderRadius: '11px',
              background: colors.loginBrand.goldGradient,
              color: colors.loginBrand.accentInk,
              boxShadow: 'none',
              '&:hover': {
                background: colors.loginBrand.goldGradient,
                boxShadow: '0 8px 24px rgba(230,138,6,0.25)',
              },
              '&.Mui-disabled': {
                opacity: 0.55,
                color: colors.loginBrand.accentInk,
                background: colors.loginBrand.goldGradient,
              },
            }}
          >
            {isSubmitting ? (
              <CircularProgress size={20} sx={{ color: colors.loginBrand.accentInk }} />
            ) : (
              'Reset password'
            )}
          </Button>

          <Typography sx={{ mt: 3, fontSize: 13.5, color: colors.loginBrand.muted, lineHeight: 1.6 }}>
            After resetting, you&apos;ll return to the sign-in page to log in with your new password.
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          position: 'relative',
          overflow: 'hidden',
          color: 'white',
          flex: 0.96,
          p: { md: 6, lg: 7.5 },
          background: colors.loginBrand.navyGradient,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: 520,
            height: 520,
            borderRadius: '50%',
            filter: 'blur(90px)',
            background: `radial-gradient(circle, ${colors.loginBrand.gold2}6B, transparent 66%)`,
            top: -180,
            right: -120,
            opacity: 0.5,
            pointerEvents: 'none',
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            width: 230,
            height: 230,
            top: -46,
            left: -46,
            borderRadius: '50%',
            opacity: 0.85,
            background: `
              radial-gradient(120% 120% at 30% 24%, #fff 0%, #EAF6FE 16%, #BFE6F8 38%, transparent 62%),
              conic-gradient(from 210deg at 62% 60%, #193F8C, #1E9BD7 30%, #8FDBF4 50%, #fff 62%, #8FDBF4 78%, #193F8C)
            `,
            boxShadow:
              'inset 0 0 60px rgba(255,255,255,0.45), 0 8px 24px rgba(34,29,21,0.10), 0 40px 80px rgba(34,29,21,0.14)',
            animation: 'floaty 9s ease-in-out infinite',
            '@keyframes floaty': {
              '0%, 100%': { transform: 'translateY(0)' },
              '50%': { transform: 'translateY(-14px)' },
            },
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />

        <Box sx={{ position: 'relative', zIndex: 2 }}>
          <Box
            component="img"
            src={pearlLoginLogo}
            alt="Pearl CRM"
            sx={{
              height: { md: 80, lg: 96 },
              width: 'auto',
              maxWidth: 420,
              objectFit: 'contain',
              display: 'block',
            }}
          />
        </Box>

        <Box sx={{ position: 'relative', zIndex: 2, my: 'auto', py: 4, maxWidth: 460 }}>
          <Typography
            sx={{
              fontFamily: '"Sora", sans-serif',
              fontSize: { md: 30, lg: 38 },
              fontWeight: 700,
              lineHeight: 1.12,
              letterSpacing: '-0.02em',
              color: 'white !important',
            }}
          >
            Secure access, restored.
          </Typography>
          <Typography sx={{ mt: 2, fontSize: 16, lineHeight: 1.65, color: 'rgba(255,255,255,0.72)' }}>
            Set a new password and get back to your pipeline, bookings, and team — all in one place.
          </Typography>
          <Box sx={{ mt: 4, display: 'grid', gap: 1.25 }}>
            {['Encrypted password reset', 'Strong password guidance', 'Back to work in minutes'].map(
              (item) => (
                <Box
                  key={item}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    bgcolor: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: '12px',
                    px: 1.5,
                    py: 1.1,
                  }}
                >
                  <CheckIcon sx={{ fontSize: 18, color: colors.loginBrand.gold1 }} />
                  <Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,0.9)' }}>{item}</Typography>
                </Box>
              )
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2.5,
            flexWrap: 'wrap',
            color: 'rgba(255,255,255,0.7)',
            fontSize: 13,
            position: 'relative',
            zIndex: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <LockOutlinedIcon sx={{ fontSize: 15, color: colors.loginBrand.gold1 }} />
            Time-limited secure link
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <ShieldOutlinedIcon sx={{ fontSize: 15, color: colors.loginBrand.gold1 }} />
            Encrypted sign-in
          </Box>
        </Box>
      </Box>

      {showSuccessAnimation && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: 'rgba(14,35,72,0.55)',
            zIndex: 9999,
          }}
        >
          <Lottie
            options={{
              loop: false,
              autoplay: true,
              animationData: successAnimation,
              rendererSettings: { preserveAspectRatio: 'xMidYMid slice' },
            }}
            height={200}
            width={200}
          />
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ResetPassword;
